<template>
  <div>
    <div class="section-header">
      <div>
        <h3 class="text-lg font-semibold">Events</h3>
      </div>
      <div class="event-actions">
        <span class="result-count">{{ eventsLoading ? 'Searching...' : `${events.length} results` }}</span>
      </div>
    </div>

    <p v-if="eventsUploadError" class="text-sm text-red-600 mb-3">{{ eventsUploadError }}</p>

    <div class="event-list">
      <template v-if="eventsLoading">
        <div
          v-for="index in skeletonCount"
          :key="`skeleton-${index}`"
          class="event-card event-card-skeleton"
          aria-hidden="true"
        >
          <div class="event-card-header">
            <div class="event-card-heading event-card-heading-skeleton">
              <span class="skeleton-block skeleton-title"></span>
              <span class="skeleton-block skeleton-pill"></span>
            </div>
            <span class="skeleton-block skeleton-time"></span>
          </div>
          <p class="skeleton-block skeleton-line"></p>
          <p class="skeleton-block skeleton-line skeleton-line-short"></p>
        </div>
      </template>

      <template v-else>
        <button
          v-for="event in events"
          :key="event.id"
          type="button"
          class="event-card"
          :class="{ 'event-card-active': event.id === selectedEventId }"
          @click="$emit('select-event', event.id)"
        >
          <div class="event-card-header">
            <div class="event-card-heading">
              <span class="event-title">{{ event.title }}</span>
              <span class="event-type-pill" :class="`event-type-pill-${event.type}`">
                {{ event.type }}
              </span>
            </div>
            <span class="event-time">{{ formatDuration(event.timestamp) }}</span>
          </div>
          <p class="event-summary">{{ event.headline }}</p>
        </button>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CeremonyEventList',
  props: {
    events: {
      type: Array,
      default: () => [],
    },
    eventsLoading: {
      type: Boolean,
      default: false,
    },
    selectedEventId: {
      type: String,
      default: '',
    },
    eventsUploadError: {
      type: String,
      default: '',
    },
    formatDuration: {
      type: Function,
      required: true,
    },
  },
  computed: {
    skeletonCount() {
      return 6
    },
  },
}
</script>

<style scoped>
.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.event-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.result-count {
  border-radius: 999px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 6px 10px;
  white-space: nowrap;
}

.event-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 760px;
  overflow-y: auto;
  padding-right: 4px;
}

.event-card {
  width: 100%;
  text-align: left;
  border: 1px solid #d6dbe5;
  border-radius: .25rem;
  padding: 14px;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.event-card:hover {
  border-color: #94a3b8;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.07);
  transform: translateY(-1px);
}

.event-card-active {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.event-card-skeleton {
  cursor: default;
  pointer-events: none;
}

.event-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.event-card-heading {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.event-card-heading-skeleton {
  flex: 1 1 auto;
}

.event-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #111827;
}

.event-type-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 8px;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
  text-transform: capitalize;
}

.event-type-pill-transcript {
  background: #dbeafe;
  color: #1d4ed8;
}

.event-type-pill-frame {
  background: #dcfce7;
  color: #166534;
}

.event-type-pill-audio {
  background: #fef3c7;
  color: #b45309;
}

.event-time {
  font-size: 0.8rem;
  color: #475569;
  white-space: nowrap;
}

.event-summary,
.event-meta {
  font-size: 0.9rem;
  line-height: 1.5;
  color: #475569;
}

.event-meta {
  margin-top: 8px;
  color: #64748b;
}

.skeleton-block {
  display: block;
  border-radius: 999px;
  background: linear-gradient(90deg, #e2e8f0 0%, #f8fafc 50%, #e2e8f0 100%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.3s ease-in-out infinite;
}

.skeleton-title {
  width: 160px;
  height: 16px;
}

.skeleton-pill {
  width: 64px;
  height: 22px;
}

.skeleton-time {
  width: 52px;
  height: 14px;
}

.skeleton-line {
  width: 100%;
  height: 13px;
  margin-top: 10px;
  border-radius: 8px;
}

.skeleton-line-short {
  width: 72%;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}
</style>
